"use client";
import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { defaultPromo } from "@/components/ProgramDetailLayout/constants";
import PromoBanner from "@/components/PromoBanner";
import {
  findMatchingPartner,
  formatPriceDisplay,
  getDisplayedCourses,
  getIndividualCourses,
  hasProgram,
} from "@/components/Navigation/multiCart/utils";
import Tags from "@/components/Tags";
import { Button } from "@/components/ui/button";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";
import useIsMobile from "@/hooks/useIsMobile";
import { useWelcomeBackModal } from "@/hooks/useWelcomeBackModal";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import type { AbandonedCartTestResult } from "@/monarch/rules/abandonedCart/types";
import {
  formatCurrency,
  CartItem,
  getProgramInfoForItem,
  firstEnrichedProgramInfo,
} from "@/utils/cartUtils";
import { getCorrelationID } from "@/utils/tagular/helpers";
import {
  formatCourseName,
  createProductViewEventData,
  createConversionEventData,
  ABANDONED_CART_EVENT_CONFIGS,
  VERIFIED_CERTIFICATE_TAG,
} from "./utils";

export default function WelcomeBackModal() {
  const isMobile = useIsMobile();
  const {
    isOpen,
    dismissModal,
    handleContinueToCheckout,
    cartData,
    getTotalItems,
  } = useWelcomeBackModal();

  const [showAllCourses, setShowAllCourses] = useState<Record<string, boolean>>(
    {},
  );

  // Computed cart values
  const items = useMemo(() => cartData?.items || [], [cartData]);
  const subtotal = cartData?.subtotal || 0;
  const total = cartData?.total || 0;
  const firstItem = items[0];
  const hasProgramItems = hasProgram(items);
  const individualCourses = getIndividualCourses(items, cartData.bundleInfoMap);
  const totalItems = getTotalItems();

  // Element event tracking
  const { elementViewed: modalViewed } = useElementEvents({
    webElement: ABANDONED_CART_EVENT_CONFIGS.modal,
  });

  const { elementClicked: ctaClicked } = useElementEvents({
    webElement: ABANDONED_CART_EVENT_CONFIGS.modalCtaButton,
  });

  const { elementClicked: closeClicked } = useElementEvents({
    webElement: ABANDONED_CART_EVENT_CONFIGS.modalCloseButton,
  });

  // Track modal view when it opens
  useEffect(() => {
    if (isOpen) {
      modalViewed();
    }
  }, [isOpen, modalViewed]);

  // Track product views for each item in the modal
  useEffect(() => {
    if (isOpen && items.length > 0) {
      items.forEach((item, index) => {
        const productViewedEvent = makeEvent(
          createProductViewEventData(
            item,
            index,
            getProgramInfoForItem(cartData, item)?.brand,
          ),
        );
        productViewedEvent(EVENTS.ProductViewed)();
      });
    }
  }, [isOpen, items, cartData]);

  const firstProgramBrandInfo = firstEnrichedProgramInfo(cartData);

  // Event tracking functions
  const handleContinueToCheckoutClick = () => {
    ctaClicked();

    // Track conversion when going to checkout
    const correlationId = getCorrelationID();
    const conversionEvent = createConversionEventData(
      items,
      firstProgramBrandInfo?.brand,
      correlationId,
      "modal_checkout",
    );

    const event = makeEvent<ConversionTracked, EVENTS>(conversionEvent);
    event<ConversionTracked>(EVENTS.ConversionTracked)();

    // Add small delay to ensure event is sent before navigation
    setTimeout(() => {
      handleContinueToCheckout();
    }, 150);
  };

  const handleDismissClick = () => {
    closeClicked();
    dismissModal();
  };

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        closeClicked();
        dismissModal();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, dismissModal, closeClicked]);

  if (!isOpen) return null;

  const handleClickOutside = (e: Event) => {
    // prevent close on mobile
    if (isMobile) {
      e.preventDefault();
    }
  };

  return (
    <MonarchDataConsumer<AbandonedCartTestResult>
      experimentKey="useAbandonedCart"
      render={(testResult) => {
        if (
          !testResult?.data?.welcomeBackModal ||
          !testResult.data.welcomeBackModal?.enabled
        ) {
          return null;
        }
        const { welcomeBackModal } = testResult.data;
        return (
          <Dialog open={isOpen} onOpenChange={handleDismissClick}>
            <DialogContent
              className="w-full max-w-[900px] flex flex-col justify-start items-center md:items-start !rounded-xl bg-putty-200 py-12 px-6 md:px-12"
              onPointerDownOutside={(e) => handleClickOutside(e)}
              onInteractOutside={(e) => handleClickOutside(e)}
              roundedCloseButton
            >
              <DialogHeader>
                <DialogTitle className="text-left text-primary elm-d1 italic font-black leading-[110%] mb-0 md:text-[3rem]">
                  {welcomeBackModal.modalHeader ??
                    `Welcome back! You have ${
                      totalItems === 1
                        ? `one ${firstItem?.bundleId ? "program" : "course"}`
                        : `${totalItems} items`
                    } in your cart.`}
                </DialogTitle>
              </DialogHeader>

              <div className="w-full">
                <h3 className="text-primary mt-0 mb-4 md:text-[1.875rem]">
                  {welcomeBackModal.modalSubHeader ||
                    "Ready to start learning?"}
                </h3>
                {firstItem && (
                  <div className="bg-white border rounded-[8px] p-4 mb-4 w-full">
                    <div className="space-y-4 mb-6">
                      {hasProgramItems ? (
                        // Program cart - show all programs
                        <div className="space-y-4">
                          {Object.entries(cartData.bundleInfoMap).map(
                            ([bundleId, bundleInfo]) => {
                              const programCourses = items.filter(
                                (item) => item.bundleId === bundleId,
                              );
                              const isShowingAll =
                                showAllCourses[bundleId || ""] || false;
                              const displayedCourses = getDisplayedCourses(
                                items,
                                bundleId,
                                showAllCourses,
                              );

                              const partner = bundleInfo.partners[0];
                              const logoUrl =
                                bundleInfo.partnerLogoOverride ||
                                partner.logoUrl;

                              const totalPrice = programCourses.reduce(
                                (n, { totalPrice, price }) =>
                                  totalPrice ? n + totalPrice : n + price,
                                0,
                              );
                              const originalPrice = programCourses.reduce(
                                (n, { originalPrice, price }) =>
                                  originalPrice ? n + originalPrice : n + price,
                                0,
                              );

                              return (
                                <div
                                  key={bundleId}
                                  className="flex justify-between items-start"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      {logoUrl && (
                                        <div className="hidden md:block">
                                          <Image
                                            key={partner.key}
                                            src={logoUrl}
                                            width={119}
                                            height={60}
                                            alt={partner.name}
                                            title={partner.name}
                                            className="max-w-[120px] object-contain border border-putty-400 rounded"
                                          />
                                        </div>
                                      )}
                                      <div className="flex-1">
                                        <p className="font-bold text-black mb-0 elm-h6">
                                          {formatCourseName(
                                            bundleInfo.title,
                                            partner.key,
                                          )}
                                        </p>
                                        <div className="py-2">
                                          <Tags
                                            variant="badges"
                                            tags={[VERIFIED_CERTIFICATE_TAG]}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      {programCourses.length > 0 && (
                                        <p className="text-black elm-p4 my-2 font-bold">
                                          This program includes{" "}
                                          {programCourses.length} course
                                          {programCourses.length > 1 ? "s" : ""}
                                          :
                                        </p>
                                      )}
                                      {programCourses.length > 0 ? (
                                        <div>
                                          <div className="flex justify-between items-center mb-1">
                                            <button
                                              onClick={() =>
                                                setShowAllCourses((prev) => ({
                                                  ...prev,
                                                  [bundleId || ""]:
                                                    !isShowingAll,
                                                }))
                                              }
                                              className="text-info underline elm-p3 flex items-center"
                                            >
                                              {isShowingAll
                                                ? "Show less"
                                                : "Show all"}
                                              <span className="ml-1 text-black no-underline">
                                                {isShowingAll ? (
                                                  <svg
                                                    className="w-5 h-5"
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                                                  </svg>
                                                ) : (
                                                  <svg
                                                    className="w-5 h-5"
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                                                  </svg>
                                                )}
                                              </span>
                                            </button>
                                            <div className="flex items-center">
                                              {originalPrice &&
                                                originalPrice >
                                                  (totalPrice || 0) && (
                                                  <span className="text-gray-500 line-through elm-p4 mr-2">
                                                    ${originalPrice.toFixed(2)}
                                                  </span>
                                                )}
                                              <span className="text-black font-normal elm-p4">
                                                {totalPrice
                                                  ? `$${totalPrice.toFixed(2)}`
                                                  : null}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex justify-end items-center">
                                          <div className="flex items-center">
                                            {originalPrice &&
                                              originalPrice >
                                                (totalPrice || 0) && (
                                                <span className="text-gray-500 line-through elm-p4 mr-2">
                                                  ${originalPrice.toFixed(2)}
                                                </span>
                                              )}
                                            <span className="text-black font-normal elm-p4">
                                              {totalPrice
                                                ? `$${totalPrice.toFixed(2)}`
                                                : null}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      {displayedCourses.map((course, index) => (
                                        <div
                                          key={course.id || index}
                                          className="flex items-start"
                                        >
                                          <span className="text-black mr-2">
                                            •
                                          </span>
                                          <span className="text-black elm-p3">
                                            {formatCourseName(
                                              course.courseName,
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      ) : null}

                      {/* Show individual courses if they exist */}
                      {individualCourses.length > 0 && (
                        <div className="space-y-4">
                          {individualCourses.map((item: CartItem) => {
                            const courseBrandInfo = getProgramInfoForItem(
                              cartData,
                              item,
                            );
                            return (
                              <div
                                key={item.id}
                                className="flex justify-between items-start"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    {/* Partner logo - inline with course name */}
                                    {(() => {
                                      const matchingPartner =
                                        findMatchingPartner(
                                          courseBrandInfo?.partners || [],
                                          courseBrandInfo?.brand || "",
                                        );
                                      return matchingPartner ? (
                                        <div className="hidden md:block">
                                          <Image
                                            key={matchingPartner.key}
                                            src={matchingPartner.logoUrl}
                                            width={119}
                                            height={60}
                                            alt={matchingPartner.name}
                                            title={matchingPartner.name}
                                            className="max-w-[120px] object-contain border border-putty-400 rounded"
                                          />
                                        </div>
                                      ) : item?.image?.url ? (
                                        <div className="hidden md:block">
                                          <Image
                                            src={item.image.url}
                                            width={119}
                                            height={60}
                                            alt={item.courseName}
                                            title={item.courseName}
                                            className="max-w-[120px] object-contain border border-putty-400 rounded"
                                          />
                                        </div>
                                      ) : null;
                                    })()}
                                    <div className="flex-1">
                                      <p className="font-bold text-black mb-2 elm-h6">
                                        {formatCourseName(
                                          item.courseName,
                                          courseBrandInfo?.brand || item.brand,
                                        )}
                                      </p>
                                      <div className="flex justify-between items-center">
                                        <div className="py-1">
                                          <Tags
                                            variant="badges"
                                            tags={[VERIFIED_CERTIFICATE_TAG]}
                                          />
                                        </div>
                                        <span className="text-black font-normal elm-p4">
                                          {
                                            formatPriceDisplay(item)
                                              .formattedCurrentPrice
                                          }
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="border-t border-gray-200 pt-4 w-full">
                      <div className="flex justify-between items-center mb-4">
                        <span className="font-semibold elm-p5 text-black">
                          Total
                        </span>
                        <div className="flex items-center ml-auto">
                          {subtotal > total && (
                            <span className="text-gray-500 line-through elm-p5 mr-2">
                              {formatCurrency(subtotal)}USD
                            </span>
                          )}
                          <span className="font-semibold elm-p5 text-black">
                            {formatCurrency(total)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {welcomeBackModal.promoBanner?.enabled && (
                  <PromoBanner
                    variant="compress"
                    title={
                      welcomeBackModal.promoBanner?.title ||
                      defaultPromo.headline
                    }
                    description={
                      welcomeBackModal.promoBanner?.subtitle ||
                      "Learn new skills and make big moves for your career."
                    }
                    discount="30"
                    discountCode="SMARTEDX25"
                    endDate="June 19, 2025"
                    endDatePrefix="Offer ends"
                    ctaElement={
                      <Button
                        variant="brand"
                        size="xl"
                        className="w-full sm:w-auto px-3 py-2 ml-2"
                        onClick={handleContinueToCheckoutClick}
                      >
                        {welcomeBackModal.promoBanner?.CTA || "Go to cart"}
                      </Button>
                    }
                  />
                )}

                {/* External CTA - Only show when banner is NOT active */}
                {!welcomeBackModal?.promoBanner?.enabled && (
                  <div className="flex justify-end ml-auto pt-2">
                    <Button
                      variant="brand"
                      size="xl"
                      className="px-3 py-2 w-full md:w-auto"
                      onClick={handleContinueToCheckoutClick}
                    >
                      {welcomeBackModal.modalCta || "Go to cart"}
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        );
      }}
    />
  );
}
